import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Users, ClipboardList, MessageCircle, Flag, ShieldCheck, Ban, Trash2, BadgeCheck, Check, Clock, Eye, Pencil, Settings, ScrollText } from "lucide-react";
import api from "@/lib/api";
import { catMap } from "@/lib/categories";
import { countryFlag } from "@/lib/places";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AdminFooterSettings from "@/components/AdminFooterSettings";
import AdminHero from "@/components/AdminHero";
import AdminHomeAd from "@/components/AdminHomeAd";
import AdminTicker from "@/components/AdminTicker";
import AdminStats from "@/components/AdminStats";
import AdminSocial from "@/components/AdminSocial";
import AdminRules from "@/components/AdminRules";
import AdminBizImport from "@/components/AdminBizImport";
import AdminPlaces from "@/components/AdminPlaces";
import AdminCategories from "@/components/AdminCategories";
import AdminTrash from "@/components/AdminTrash";
import AdminBadWords from "@/components/AdminBadWords";
import AdminPoints from "@/components/AdminPoints";
import AdminQr from "@/components/AdminQr";
import AdminSuggestions from "@/components/AdminSuggestions";
import { can, isAdminLike } from "@/lib/perms";

const TAB_PERM = { tasks: "any", pending: "listings", verifications: "verifications", reports: "reports", listings: "listings", users: "users", posts: "posts", logs: "main",
  categories: "categories", places: "places", "places-reklame": "places", footer: "settings", hero: "settings", homead: "settings", ticker: "settings", stats: "stats",
  social: "stats", rules: "settings", badwords: "settings", points: "points", qr: "points", bizimport: "bizimport", suggest: "bizimport", trash: "trash" };
import { toast } from "sonner";
import AdminTasks from "@/components/AdminTasks";

const TONE = {
  high: "bg-rose-500 text-white data-[state=active]:bg-rose-600 data-[state=active]:text-white",
  mid: "bg-sky-100 text-sky-800 data-[state=active]:bg-sky-200 data-[state=active]:text-sky-900",
  low: "bg-white text-slate-600 data-[state=active]:bg-white data-[state=active]:text-slate-900",
};
const ADMIN_TABS = [
  ["tasks", "Detyrat", "high"], ["pending", "Pritje", "high"], ["verifications", "Verifikime", "high", "verif"], ["reports", "Raporte", "high"],
  ["suggest", "Biznese të treguara", "high"], ["users", "Përdorues", "mid"], ["listings", "Publikime", "mid"], ["posts", "Biseda", "mid"],
  ["points", "GjejKredit", "mid"], ["qr", "QR", "mid"], ["bizimport", "Gjej biznes", "mid"], ["trash", "Koshi", "mid"],
  ["logs", "Log", "low"], ["categories", "Kategori", "low"], ["places", "Shtete", "low"], ["places-reklame", "Shtete Reklamë", "low"],
  ["footer", "Footer", "low"], ["hero", "Kreu i faqes", "low"], ["homead", "Reklama në Krye", "low"], ["ticker", "Shiriti i tekstit", "low"], ["stats", "Statistikat", "low"],
  ["social", "Rrjete sociale", "low"], ["rules", "Rregullat", "low"], ["badwords", "Fjalë të ndaluara", "low"],
];

export default function AdminPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [uq, setUq] = useState("");
  const [listings, setListings] = useState([]);
  const [reports, setReports] = useState([]);
  const [verifs, setVerifs] = useState([]);
  const [posts, setPosts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [staffOnly, setStaffOnly] = useState(false);
  const ok = (t) => (TAB_PERM[t] === "any" ? true : TAB_PERM[t] === "main" ? user?.role === "admin" : can(user, TAB_PERM[t]));

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    if (can(user, "users")) api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    if (can(user, "listings")) api.get("/admin/listings").then((r) => setListings(r.data)).catch(() => {});
    if (can(user, "reports")) api.get("/admin/reports").then((r) => setReports(r.data)).catch(() => {});
    if (can(user, "verifications")) api.get("/admin/verifications").then((r) => setVerifs(r.data)).catch(() => {});
    if (can(user, "posts")) api.get("/admin/posts").then((r) => setPosts(r.data)).catch(() => {});
    if (user?.role === "admin") api.get("/admin/logs").then((r) => setLogs(r.data)).catch(() => {});
  };
  useEffect(() => { if (isAdminLike(user)) loadAll(); }, [user]); // eslint-disable-line

  if (user && user !== false && !isAdminLike(user)) return <div className="p-8 text-center text-slate-500">Vetëm admini ka qasje.</div>;
  if (!stats) return <div className="p-8 text-center text-slate-400">Duke ngarkuar...</div>;

  const block = async (id) => { const r = await api.post(`/admin/users/${id}/block`); toast.success(r.data.blocked ? "U bllokua" : "U zhbllokua"); loadAll(); };
  const verify = async (id) => { await api.post(`/admin/listings/${id}/verify`); toast.success("U përditësua"); loadAll(); };
  const delListing = async (id) => { await api.delete(`/admin/listings/${id}`); toast.success("U fshi"); loadAll(); };
  const resolve = async (id) => { await api.post(`/admin/reports/${id}/resolve`); toast.success("U zgjidh"); loadAll(); };
  const approve = async (id) => { const r = await api.post(`/admin/listings/${id}/approve`); toast.success(r.data.changes ? "Ndryshimet u aprovuan" : "U aprovua & publikua"); loadAll(); };
  const reject = async (id) => { const r = await api.post(`/admin/listings/${id}/reject`); toast.success(r.data.changes ? "Ndryshimet u refuzuan, mbetet versioni i vjetër" : "U refuzua"); loadAll(); };
  const delPost = async (id) => { if (!window.confirm("Fshi këtë postim? Autori do të njoftohet se nuk përputhet me rregullat e GjejShqip.")) return; await api.delete(`/admin/posts/${id}`); toast.success("Postimi u fshi dhe autori u njoftua"); loadAll(); };
  const openListing = (l) => nav(l.category === "punekerkues" ? `/punekerkues/${l.id}` : `/shpallje/${l.id}${l.has_pending_changes ? "?draft=1" : ""}`);
  const pending = listings.filter((l) => l.status === "pending" || l.has_pending_changes);

  const cards = [
    { label: "Përdorues", value: stats.users, icon: Users, color: "text-emerald-600 bg-emerald-50" },
    { label: "Publikime", value: stats.listings, icon: ClipboardList, color: "text-blue-600 bg-blue-50" },
    { label: "Në pritje", value: stats.pending, icon: Clock, color: "text-amber-600 bg-amber-50" },
    { label: "Biseda", value: stats.posts, icon: MessageCircle, color: "text-cyan-600 bg-cyan-50" },
    { label: "Komente", value: stats.comments, icon: MessageCircle, color: "text-purple-600 bg-purple-50" },
    { label: "Raportime", value: stats.reports, icon: Flag, color: "text-red-600 bg-red-50" },
    { label: "Verifikime", value: stats.verifications, icon: ShieldCheck, color: "text-amber-600 bg-amber-50" },
  ];

  return (
    <div className="gs-fade px-4 pt-4 pb-6">
      <button onClick={() => nav("/profil")} className="flex items-center gap-1 text-sm text-slate-500 mb-2"><ArrowLeft className="w-4 h-4" />Profili</button>
      <h1 className="flex items-center gap-2 text-2xl font-extrabold text-slate-900 mb-3"><ShieldCheck className="w-6 h-6 text-slate-900" />Admin Panel</h1>

      <div className="grid grid-cols-3 gap-2">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} data-testid={`stat-${c.label}`} className="bg-white rounded-2xl p-3 border border-slate-100">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${c.color}`}><Icon className="w-4 h-4" /></div>
              <div className="text-2xl font-extrabold text-slate-900 mt-1.5">{c.value}</div>
              <div className="text-xs text-slate-400">{c.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-4 gap-1.5 mt-3">
        {Object.entries(stats.by_category).map(([k, v]) => {
          const cat = catMap[k] || { light: "bg-slate-100", text: "text-slate-600", label: k };
          return <div key={k} className={`${cat.light} ${cat.text} rounded-xl px-2 py-1.5 text-center`}><div className="font-bold text-sm">{v}</div><div className="text-[10px] font-medium truncate">{cat.label}</div></div>;
        })}
      </div>

      <Tabs defaultValue={Object.keys(TAB_PERM).find(ok) || "pending"} className="mt-4">
        <TabsList className="w-full grid grid-cols-3 gap-1.5 h-auto rounded-2xl p-1.5 bg-slate-100 [&>[data-hide=true]]:hidden">
          {ADMIN_TABS.map(([v, label, tone, tid]) => (
            <TabsTrigger key={v} data-hide={!ok(v)} value={v} data-testid={`admin-tab-${tid || v}`}
              className={`min-h-[46px] px-1.5 py-2 rounded-xl text-[13px] font-bold whitespace-normal leading-tight text-center transition-transform active:scale-95 data-[state=active]:ring-2 data-[state=active]:ring-slate-900 data-[state=active]:shadow-md ${TONE[tone]}`}>{label}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="tasks" className="mt-3">
          <AdminTasks />
        </TabsContent>

        <TabsContent value="pending" className="mt-3 space-y-2">
          {pending.map((l) => (
            <div key={l.id} className="bg-white rounded-xl p-2.5 border border-slate-100">
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0" onClick={() => openListing(l)}><div className="font-semibold text-sm text-slate-800 truncate">{l.has_pending_changes && <span data-testid={`admin-changes-badge-${l.id}`} className="mr-1 text-[10px] bg-blue-100 text-blue-700 font-semibold px-1.5 py-0.5 rounded">Ndryshim</span>}{l.title}</div><span className="text-xs text-slate-400">{catMap[l.category]?.label || l.category} · {l.city}</span></div>
                <button data-testid={`admin-open-${l.id}`} onClick={() => openListing(l)} className="text-slate-500 p-1.5"><Eye className="w-4 h-4" /></button>
                <button data-testid={`admin-edit-${l.id}`} onClick={() => nav(`/ndrysho/${l.id}`)} className="text-slate-500 p-1.5"><Pencil className="w-4 h-4" /></button>
              </div>
              <div className="flex gap-2 mt-2">
                <button data-testid={`admin-approve-${l.id}`} onClick={() => approve(l.id)} className="flex-1 bg-emerald-500 text-white rounded-lg py-2 text-xs font-bold flex items-center justify-center gap-1"><Check className="w-3.5 h-3.5" />Aprovo & publiko</button>
                <button data-testid={`admin-reject-${l.id}`} onClick={() => reject(l.id)} className="bg-red-50 text-red-500 rounded-lg px-3 py-2 text-xs font-semibold">Refuzo</button>
                <button data-testid={`admin-del-${l.id}`} onClick={() => delListing(l.id)} className="bg-slate-100 text-slate-500 rounded-lg px-3 py-2"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
          {!pending.length && <p className="text-center text-slate-400 py-6 text-sm">Asnjë publikim në pritje. 🎉</p>}
        </TabsContent>

        <TabsContent value="verifications" className="mt-3 space-y-2">
          {verifs.map((v) => (
            <div key={v.id} className="flex items-center gap-2 bg-white rounded-xl p-3 border border-slate-100">
              <div className="flex-1"><div className="font-semibold text-sm text-slate-800">{v.listing_title}</div><div className="text-xs text-slate-400">Kërkesë verifikimi</div></div>
              <button data-testid={`approve-verif-${v.listing_id}`} onClick={() => verify(v.listing_id)} className="bg-emerald-500 text-white rounded-lg px-3 py-1.5 text-xs font-semibold flex items-center gap-1"><Check className="w-3.5 h-3.5" />Aprovo</button>
            </div>
          ))}
          {!verifs.length && <p className="text-center text-slate-400 py-6 text-sm">Pa kërkesa verifikimi.</p>}
        </TabsContent>

        <TabsContent value="reports" className="mt-3 space-y-2">
          {reports.map((r) => (
            <div key={r.id} className="flex items-center gap-2 bg-white rounded-xl p-3 border border-slate-100">
              <Flag className="w-4 h-4 text-red-400" />
              <div className="flex-1 min-w-0"><div className="font-semibold text-sm text-slate-800">{r.reason}</div>{r.note && <div data-testid={`report-note-${r.id}`} className="text-sm text-slate-600 whitespace-pre-wrap break-words mt-0.5">{r.note}</div>}<div className="text-xs text-slate-400">{r.target_type === "problem" ? "Problem në faqe" : r.target_type} · nga {r.reporter_name} · {r.status}</div></div>
              {r.status === "open" && <button data-testid={`resolve-report-${r.id}`} onClick={() => resolve(r.id)} className="text-emerald-600 text-xs font-semibold px-2">Zgjidh</button>}
            </div>
          ))}
          {!reports.length && <p className="text-center text-slate-400 py-6 text-sm">Pa raportime.</p>}
        </TabsContent>

        <TabsContent value="listings" className="mt-3 space-y-2">
          {listings.slice(0, 50).map((l) => (
            <div key={l.id} className="flex items-center gap-2 bg-white rounded-xl p-2.5 border border-slate-100">
              <div className="flex-1 min-w-0" onClick={() => openListing(l)}><div className="flex items-center gap-1"><span className="font-semibold text-sm text-slate-800 truncate">{l.title}</span>{l.verified && <BadgeCheck className="w-4 h-4 text-emerald-500 shrink-0" />}{l.status !== "active" && <span className="text-[10px] bg-amber-100 text-amber-700 font-semibold px-1.5 py-0.5 rounded shrink-0">{l.status === "pending" ? "pritje" : l.status}</span>}</div><span className="text-xs text-slate-400">{catMap[l.category]?.label || l.category} · {l.city}</span></div>
              <button data-testid={`admin-edit2-${l.id}`} onClick={() => nav(`/ndrysho/${l.id}`)} className="text-slate-500 p-1.5"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`admin-verify-${l.id}`} onClick={() => verify(l.id)} className="text-emerald-600 p-1.5"><ShieldCheck className="w-4 h-4" /></button>
              <button data-testid={`admin-delete-${l.id}`} onClick={() => delListing(l.id)} className="text-red-400 p-1.5"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="users" className="mt-3 space-y-2">
          <input data-testid="admin-user-search" value={uq} onChange={(e) => setUq(e.target.value)} placeholder="Kërko me numër (#15), emër ose email" className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm bg-white" />
          {users.filter((u) => {
            const s = uq.trim().toLowerCase();
            if (!s) return true;
            const n = s.replace(/^#/, "");
            if (/^\d+$/.test(n)) return u.member_no === parseInt(n, 10);
            return `${u.name} ${u.surname} ${u.email}`.toLowerCase().includes(s);
          }).map((u) => (
            <div key={u.id} data-testid={`admin-user-${u.id}`} onClick={() => nav(`/admin/user/${u.id}`)} className="flex items-center gap-2 bg-white rounded-xl p-2.5 border border-slate-100 cursor-pointer hover:border-emerald-200">
              <div className="flex flex-col items-center gap-0.5 shrink-0">
                {u.member_no != null && <span data-testid={`admin-user-no-${u.id}`} className="text-[10px] font-extrabold text-emerald-700 bg-emerald-50 px-1.5 rounded">#{String(u.member_no).padStart(2, "0")}</span>}
                <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-sm overflow-hidden">{u.avatar ? <img src={u.avatar} alt="" className="w-full h-full object-cover" /> : u.name?.[0]}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-slate-800 truncate"><span translate="no">{u.name} {u.surname}</span> {u.role === "admin" && <span className="text-[10px] bg-slate-900 text-white px-1.5 py-0.5 rounded">admin</span>}{u.role === "staff" && <span className="text-[10px] bg-slate-900 text-white px-1.5 py-0.5 rounded">admin</span>}</div>
                <div className="text-xs text-slate-400 truncate">{u.email}</div>
                <div className="text-[11px] text-slate-400">{countryFlag(u.country)} {u.city || u.country || "—"} · {u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</div>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${u.blocked ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-700"}`}>{u.blocked ? "Bllokuar" : "Aktiv"}</span>
              <Eye className="w-4 h-4 text-slate-300 shrink-0" />
            </div>
          ))}
        </TabsContent>

        <TabsContent value="logs" className="mt-3 space-y-2">
          <button data-testid="logs-staff-only" onClick={() => setStaffOnly((v) => !v)} className={`h-8 px-3 rounded-full text-xs font-semibold border ${staffOnly ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200"}`}>Vetëm adminat e rinj</button>
          {logs.filter((lg) => !staffOnly || lg.staff).map((lg) => (
            <div key={lg.id} className="bg-white rounded-xl p-2.5 border border-slate-100">
              <div className="text-sm text-slate-800 break-words">{lg.staff && <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded mr-1">Admin i ri</span>}<span className="font-semibold">{lg.staff ? lg.action : lg.action.replace("_", " ")}</span> {lg.target_type && <span className="text-slate-400">· {lg.target_type}</span>}</div>
              <div className="text-[11px] text-slate-400">nga {lg.admin_name} · {lg.created_at ? new Date(lg.created_at).toLocaleString() : ""}{lg.detail ? ` · ${lg.detail}` : ""}</div>
            </div>
          ))}
          {!logs.length && <p className="text-center text-slate-400 py-6 text-sm">Pa veprime të regjistruara.</p>}
        </TabsContent>

        <TabsContent value="posts" className="mt-3 space-y-2">
          {posts.slice(0, 100).map((p) => (
            <div key={p.id} className="bg-white rounded-xl p-2.5 border border-slate-100">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0" onClick={() => nav(`/komunitete/${p.id}`)}>
                  <div className="font-semibold text-sm text-slate-800 truncate">{p.title}</div>
                  <div className="text-xs text-slate-500 line-clamp-2">{p.body}</div>
                  <div className="text-[11px] text-slate-400 mt-1">{p.topic} · {p.city}, {p.country} · {p.author_name}</div>
                </div>
                <button data-testid={`admin-del-post-${p.id}`} onClick={() => delPost(p.id)} className="text-red-400 p-1.5 shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {!posts.length && <p className="text-center text-slate-400 py-6 text-sm">Pa biseda.</p>}
        </TabsContent>

        <TabsContent value="categories" className="mt-3">
          <AdminCategories />
        </TabsContent>
        <TabsContent value="places" className="mt-3">
          <AdminPlaces />
        </TabsContent>
        <TabsContent value="places-reklame" className="mt-3">
          <AdminPlaces kind="reklame" />
        </TabsContent>
        <TabsContent value="footer" className="mt-3">
          <AdminFooterSettings />
        </TabsContent>
        <TabsContent value="hero" className="mt-3">
          <AdminHero />
        </TabsContent>
        <TabsContent value="homead" className="mt-3">
          <AdminHomeAd />
        </TabsContent>
        <TabsContent value="ticker" className="mt-3">
          <AdminTicker />
        </TabsContent>
        <TabsContent value="trash" className="mt-3">
          <AdminTrash />
        </TabsContent>
        <TabsContent value="bizimport" className="mt-3">
          <AdminBizImport />
        </TabsContent>
        <TabsContent value="points" className="mt-3">
          <AdminPoints />
        </TabsContent>
        <TabsContent value="qr" className="mt-3">
          <AdminQr />
        </TabsContent>
        <TabsContent value="suggest" className="mt-3">
          <AdminSuggestions />
        </TabsContent>
        <TabsContent value="badwords" className="mt-3">
          <AdminBadWords />
        </TabsContent>
        <TabsContent value="rules" className="mt-3">
          <AdminRules />
        </TabsContent>
        <TabsContent value="social" className="mt-3">
          <AdminSocial />
        </TabsContent>
        <TabsContent value="stats" className="mt-3">
          <AdminStats />
        </TabsContent>
      </Tabs>
    </div>
  );
}
